// Wallet Standard bridge for Meta Launchpad.
//
// Deliberately narrow: discover wallets, connect, report the account, and ask the wallet to SIGN raw
// transaction bytes, or sign the server's sign-in message. It never calls signAndSendTransaction, so broadcasting always goes through the server,
// where feature flags, duplicate-submission guards and status tracking live. No keys ever pass through here.
import { getWallets } from '@wallet-standard/app';
import { SolanaSignMessage, SolanaSignTransaction } from '@solana/wallet-standard-features';

const CONNECT = 'standard:connect';
const DISCONNECT = 'standard:disconnect';
const EVENTS = 'standard:events';
export const CHAINS = { 'mainnet-beta': 'solana:mainnet', mainnet: 'solana:mainnet', devnet: 'solana:devnet', testnet: 'solana:testnet' };
const LS_KEY = 'ml.wallet';

export function isSolanaWallet(w) {
  return !!(w && w.features && w.features[CONNECT] && w.features[SolanaSignTransaction] &&
    (w.chains || []).some(c => String(c).startsWith('solana:')));
}

/** Does this wallet/account declare support for the chain the server is configured for? */
export function supportsChain(wallet, account, chain) {
  const declared = (account && account.chains && account.chains.length ? account.chains : wallet.chains) || [];
  return declared.includes(chain);
}

export function b64ToBytes(b64) { const s = atob(b64); const out = new Uint8Array(s.length); for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i); return out; }
export function bytesToB64(bytes) { let s = ''; for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]); return btoa(s); }

/** Normalise wallet rejections so the UI can tell "you said no" from a real failure. */
const B58 = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';
export function base58(bytes) {
  let n = 0n; for (const b of bytes) n = n * 256n + BigInt(b);
  let s = ''; while (n > 0n) { s = B58[Number(n % 58n)] + s; n /= 58n; }
  for (const b of bytes) { if (b !== 0) break; s = '1' + s; }
  return s;
}

/** A throwaway Ed25519 keypair for a new coin's mint, made by the browser (WebCrypto). The private key is
 *  non-extractable and lives only in this tab's memory: it never reaches Atlas's server. Used so the user's wallet
 *  can sign a launch FIRST and the mint signature is added after (Phantom blocks pre-signed requests). */
export async function createMintSigner(subtle = globalThis.crypto?.subtle) {
  if (!subtle) throw Object.assign(new Error('This browser cannot create the coin key (no WebCrypto). Update your browser.'), { kind: 'unsupported' });
  let kp;
  try { kp = await subtle.generateKey({ name: 'Ed25519' }, false, ['sign']); }
  catch { throw Object.assign(new Error('This browser cannot create the coin key (no Ed25519 support). Update your browser and try again.'), { kind: 'unsupported' }); }
  const pub = new Uint8Array(await subtle.exportKey('raw', kp.publicKey));
  return { address: base58(pub), publicKey: pub, sign: async msg => new Uint8Array(await subtle.sign({ name: 'Ed25519' }, kp.privateKey, msg)) };
}

function shortvec(bytes, o) { let len = 0, shift = 0, b; do { b = bytes[o++]; len |= (b & 0x7f) << shift; shift += 7; } while (b & 0x80); return [len, o]; }

/** Put `signer`'s signature into its slot of a (wallet-signed) transaction. Only the empty slot of that exact
 *  required signer is filled; the message and every other signature are left untouched. */
export async function addSignature(txB64, signer) {
  const tx = b64ToBytes(txB64);
  const [nSig, sigStart] = shortvec(tx, 0);
  const msgStart = sigStart + 64 * nSig;
  const msg = tx.subarray(msgStart);
  let o = (msg[0] & 0x80) ? 1 : 0;              // versioned message prefix
  const nRequired = msg[o]; o += 3;             // header: required signatures, readonly signed, readonly unsigned
  const [nKeys, kStart] = shortvec(msg, o);
  if (nRequired !== nSig || nRequired > nKeys) throw Object.assign(new Error('Unexpected transaction layout.'), { kind: 'layout' });
  let slot = -1;
  for (let i = 0; i < nRequired; i++) {
    const k = msg.subarray(kStart + 32 * i, kStart + 32 * (i + 1));
    if (k.every((b, j) => b === signer.publicKey[j])) { slot = i; break; }
  }
  if (slot < 0) throw Object.assign(new Error('The coin key is not a signer of this transaction.'), { kind: 'layout' });
  const at = sigStart + 64 * slot;
  if (tx.subarray(at, at + 64).some(b => b !== 0)) throw Object.assign(new Error('The coin key already signed this transaction.'), { kind: 'layout' });
  const out = new Uint8Array(tx);
  out.set(await signer.sign(msg), at);
  return bytesToB64(out);
}

export function classifyError(e) {
  const msg = String((e && (e.message || e.toString())) || 'Unknown wallet error');
  const code = e && (e.code ?? e.error?.code);
  if (code === 4001 || /reject|denied|declined|cancel/i.test(msg)) return { kind: 'rejected', message: 'You declined the request in your wallet.' };
  if (/locked/i.test(msg)) return { kind: 'locked', message: 'Your wallet is locked. Unlock it and try again.' };
  return { kind: 'error', message: msg };
}

export function createWalletManager({ chain = 'solana:mainnet', registry = null, storage = globalThis.localStorage } = {}) {
  const api = registry || getWallets();
  let wallet = null, account = null, offEvents = null;
  const listeners = new Set();
  const emit = () => listeners.forEach(fn => { try { fn(state()); } catch { /* listener errors must not break wallet flow */ } });

  function list() { return api.get().filter(isSolanaWallet).map(w => ({ name: w.name, icon: w.icon, chainOk: (w.chains || []).includes(chain) })); }
  function state() {
    return { connected: !!account, wallet: wallet ? { name: wallet.name, icon: wallet.icon } : null,
      address: account ? account.address : null, chain, chainOk: wallet && account ? supportsChain(wallet, account, chain) : null,
      canSignMessage: !!(wallet && wallet.features[SolanaSignMessage]) };
  }
  function attach(w, accounts) {
    wallet = w; account = (accounts || w.accounts || [])[0] || null;
    offEvents?.(); offEvents = null;
    if (w.features[EVENTS]) offEvents = w.features[EVENTS].on('change', ({ accounts: acc }) => {
      if (acc) { account = acc[0] || null; if (!account) wallet = null; emit(); }
    });
    try { account ? storage?.setItem(LS_KEY, w.name) : storage?.removeItem(LS_KEY); } catch { /* storage may be blocked */ }
    emit();
  }
  async function connect(name, { silent = false } = {}) {
    const w = api.get().find(x => x.name === name && isSolanaWallet(x));
    if (!w) throw new Error(`${name} is not installed or does not support Solana signing.`);
    const res = await w.features[CONNECT].connect(silent ? { silent: true } : undefined);
    attach(w, res && res.accounts);
    if (!account) throw new Error('The wallet did not share an account.');
    return state();
  }
  async function disconnect() {
    const w = wallet; wallet = null; account = null; offEvents?.(); offEvents = null;
    try { storage?.removeItem(LS_KEY); } catch { /* ignore */ }
    try { await w?.features[DISCONNECT]?.disconnect(); } catch { /* some wallets do not implement disconnect */ }
    emit();
  }
  /** Ask the wallet to sign. Returns base64 signed transaction bytes. Never broadcasts. */
  async function signTransaction(txB64, { expectedSigner } = {}) {
    if (!wallet || !account) throw Object.assign(new Error('Connect a wallet first.'), { kind: 'not_connected' });
    if (expectedSigner && expectedSigner !== account.address)
      throw Object.assign(new Error('This transaction was built for a different wallet address. Rebuild it for the connected wallet.'), { kind: 'wrong_signer' });
    if (!supportsChain(wallet, account, chain))
      throw Object.assign(new Error(`${wallet.name} does not report support for ${chain}. Switch its network and reconnect.`), { kind: 'network' });
    const [out] = await wallet.features[SolanaSignTransaction].signTransaction({ account, chain, transaction: b64ToBytes(txB64) });
    return bytesToB64(out.signedTransaction);
  }
  /** Sign-in: ask the wallet to sign the server's message text (no transaction, no fee). Returns the base64
   *  ed25519 signature. Fails if the wallet signed different bytes than it was given, since the server only
   *  accepts a signature over the exact message it issued. */
  async function signMessage(text) {
    if (!wallet || !account) throw Object.assign(new Error('Connect a wallet first.'), { kind: 'not_connected' });
    const f = wallet.features[SolanaSignMessage];
    if (!f) throw Object.assign(new Error(`${wallet.name} cannot sign messages, so it cannot be used to sign in.`), { kind: 'unsupported' });
    const message = new TextEncoder().encode(text);
    const [out] = await f.signMessage({ account, message });
    const signed = out.signedMessage || message;
    if (signed.length !== message.length || signed.some((b, i) => b !== message[i]))
      throw Object.assign(new Error('The wallet changed the sign-in message before signing it.'), { kind: 'modified' });
    return bytesToB64(out.signature);
  }
  async function restore() {
    let name = null; try { name = storage?.getItem(LS_KEY); } catch { /* ignore */ }
    if (!name) return state();
    try { return await connect(name, { silent: true }); } catch { return state(); }
  }
  api.on('register', () => emit());
  api.on('unregister', () => emit());
  return { list, state, connect, disconnect, signTransaction, signMessage, restore, onChange: fn => (listeners.add(fn), () => listeners.delete(fn)) };
}
